import SaveIcon from '@mui/icons-material/Save';
import { Alert, Card, CardActions, CardContent, CardHeader, Divider, Grid } from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
    DefaultValues,
    FormContainer,
    MultiSelectElement,
    PasswordElement,
    PasswordRepeatElement,
    TextFieldElement,
} from 'react-hook-form-mui';
import { useHistory } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { settingsRoutes } from '../../../../routes';
import { useCreateUser, useUpdateUser } from '../../../../sic-lib/api/hooks/useUser';
import { selectableUserRoles } from '../../../../sic-lib/defaults';
import {
    buildEditUserFormState,
    EditUserData,
    initialNewUserFormState,
    NewUserData,
} from '../../../../sic-lib/types/NewEditUser';
import { getFriendlyRoleName } from '../../../../sic-lib/types/UserRoles';
import { useAppDispatch } from '../../../../store/hooks';
import { requestConfig } from '../../../../store/users';
import NavLinkButton from '../../../Navigation/NavLinkButton/NavLinkButton';
import AxiosErrorAlert from '../../../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../../../Shared/ButtonLoading';
import PhoneNumberTextFieldElement from '../../../Shared/FormControls/Inputs/PhoneNumberTextFieldElement';
import { EditUserAvatarControl } from './EditUserAvatarControl';

interface Props {
    user: EditUserData | undefined;
}

export default function CreateEditUser<T extends NewUserData | EditUserData>({ user }: Props) {
    const { classes } = useStyles();
    const history = useHistory();
    const dispatch = useAppDispatch();

    const {
        mutate: handleCreate,
        isPending: isCreating,
        isSuccess: isCreatingSuccess,
        isError: isCreatingError,
        error: createError,
    } = useCreateUser();

    const {
        mutate: handleUpdate,
        isPending: isUpdating,
        isSuccess: isUpdateSuccess,
        isError: isUpdatingError,
        error: updateError,
    } = useUpdateUser();

    const isLoading = user ? isUpdating : isCreating;
    const isSuccess = user ? isUpdateSuccess : isCreatingSuccess;
    const isError = user ? isUpdatingError : isCreatingError;
    const error = user ? updateError : createError;

    const handleSuccess = (data?: T) => {
        console.log('handleSuccess', data);
        if (user) {
            handleUpdate(data as EditUserData, {
                onSuccess: () => {
                    dispatch(requestConfig());
                },
            });
        } else {
            handleCreate(data as NewUserData, {
                onSuccess: data => {
                    dispatch(requestConfig());
                    history.push(settingsRoutes.setCreateEditUser(data.userName));
                },
            });
        }
    };

    const roleOptions = selectableUserRoles.map(role => ({
        id: role,
        label: getFriendlyRoleName(role),
    }));

    console.log('user roles', user?.roles);

    return (
        <FormContainer<T>
            onSuccess={handleSuccess}
            defaultValues={
                user
                    ? (buildEditUserFormState(user) as DefaultValues<T>)
                    : (initialNewUserFormState as DefaultValues<T>)
            }
            FormProps={{
                'aria-autocomplete': 'none',
                autoComplete: 'new-password',
            }}
            mode="all"
        >
            <Card>
                <CardHeader
                    title={user ? 'Edit User' : 'Create User'}
                    subheader={user ? user.userName : undefined}
                />
                <Divider />
                <Grid container>
                    <Grid item xs>
                        <CardContent className={classes.avatar}>
                            <EditUserAvatarControl name="colour" />
                        </CardContent>
                    </Grid>
                    <Divider orientation="vertical" flexItem />
                    <Grid item lg={8} xl={8} md={8}>
                        <CardContent>
                            <Grid container spacing={2}>
                                {!user && (
                                    <>
                                        <Grid item xs={12}>
                                            <TextFieldElement
                                                name="userName"
                                                label="User Name"
                                                fullWidth
                                                required
                                                validation={{
                                                    minLength: {
                                                        value: 3,
                                                        message: 'User Name is too short.',
                                                    },
                                                    maxLength: {
                                                        value: 250,
                                                        message: 'User Name is too long.',
                                                    },
                                                    pattern: {
                                                        value: /^[a-zA-Z0-9-._@+]+$/,
                                                        message:
                                                            'User Name contains invalid characters.',
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <PasswordElement
                                                name="password"
                                                label="Password"
                                                fullWidth
                                                required
                                                type="new-password"
                                                validation={{
                                                    minLength: {
                                                        value: 6,
                                                        message: 'Password is too short.',
                                                    },
                                                    maxLength: {
                                                        value: 250,
                                                        message: 'Password is too long.',
                                                    },
                                                    pattern: {
                                                        value: /^(?=.*\d).{6,}$/,
                                                        message: 'Password is invalid.',
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <PasswordRepeatElement
                                                passwordFieldName="password"
                                                name="password-repeat"
                                                label="Repeat Password"
                                                fullWidth
                                                required
                                                validation={{
                                                    maxLength: {
                                                        value: 250,
                                                        message: 'Password is too long.',
                                                    },
                                                }}
                                            />
                                        </Grid>
                                    </>
                                )}
                                <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                                    <TextFieldElement
                                        name="firstName"
                                        label="First Name"
                                        fullWidth
                                        required
                                        validation={{
                                            maxLength: {
                                                value: 250,
                                                message: 'Last Name is too long',
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                                    <TextFieldElement
                                        name="lastName"
                                        label="Last Name"
                                        fullWidth
                                        required
                                        validation={{
                                            maxLength: {
                                                value: 250,
                                                message: 'Last Name is too long',
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextFieldElement
                                        name="email"
                                        label="Email"
                                        type="email"
                                        fullWidth
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <PhoneNumberTextFieldElement
                                        name="phoneNumber"
                                        label="Phone Number"
                                        fullWidth
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <MultiSelectElement
                                        label="Roles"
                                        name="roles"
                                        options={roleOptions}
                                        required
                                        fullWidth
                                        showChips
                                        showCheckbox
                                        sx={{
                                            '& .MuiSelect-select > div': {
                                                gap: '4px',
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Alert severity="warning">
                                        Selecting a <strong>Read Only</strong> role will limit the
                                        user's ability to interact with certain features. Any
                                        related <strong>Manage</strong> role will be automatically
                                        removed.
                                    </Alert>
                                </Grid>
                                {error && (
                                    <Grid item xs={12}>
                                        <AxiosErrorAlert error={error} />
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Grid>
                </Grid>
                <Divider />
                <CardActions>
                    <NavLinkButton pathname={settingsRoutes.users}>Back to Users</NavLinkButton>
                    <ButtonLoading
                        variant="contained"
                        color="primary"
                        type="submit"
                        isLoading={isLoading}
                        disabled={isLoading}
                        isError={isError}
                        isSuccess={isSuccess}
                        startIcon={<SaveIcon />}
                    >
                        {user ? 'Save' : 'Create'}
                    </ButtonLoading>
                </CardActions>
            </Card>
        </FormContainer>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    avatar: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
}));
